/**
 * useVoiceCapture — browser MediaRecorder hook for the in-app chat mic
 * (2026-05-18b, Anil).
 *
 * The portal chat had a dead mic. This hook records audio in the browser
 * (getUserMedia → MediaRecorder), stops cleanly, and hands back a single
 * audio Blob. The CALLER then sends that blob to `transcribeInAppVoice`
 * (the SAME locked Whisper path WhatsApp uses) and feeds the transcript to
 * the agent exactly like a typed message — the raw blob is the canonical
 * input (verbatim-sacrosanct).
 *
 * Graceful by contract:
 *  - mic permission denied / no device  → status:'denied', clear message,
 *    never throws (the user can still type).
 *  - MediaRecorder unsupported          → status:'unsupported'.
 *  - the stream tracks are always stopped on stop/unmount (no hot mic).
 *
 * Pure React + DOM only — no Firebase. Unit-tested logic lives in
 * lib/localChat (transcribeInAppVoice); this hook is the thin DOM shell.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

export type VoiceStatus =
  | 'idle'
  | 'recording'
  | 'denied'
  | 'unsupported'
  | 'error';

export interface VoiceCapture {
  status: VoiceStatus;
  /** human, non-coder-friendly line for the UI when something is wrong. */
  message: string;
  start: () => Promise<void>;
  /** resolves with the recorded blob (or null if nothing usable). */
  stop: () => Promise<Blob | null>;
  cancel: () => void;
}

export function useVoiceCapture(): VoiceCapture {
  const [status, setStatus] = useState<VoiceStatus>('idle');
  const [message, setMessage] = useState('');
  const recRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);

  const teardown = useCallback(() => {
    try {
      streamRef.current?.getTracks().forEach((t) => t.stop());
    } catch {
      /* ignore */
    }
    streamRef.current = null;
    recRef.current = null;
    chunksRef.current = [];
  }, []);

  useEffect(() => teardown, [teardown]);

  const start = useCallback(async () => {
    if (status === 'recording') return;
    const md =
      typeof navigator !== 'undefined'
        ? navigator.mediaDevices
        : undefined;
    if (
      !md ||
      typeof md.getUserMedia !== 'function' ||
      typeof MediaRecorder === 'undefined'
    ) {
      setStatus('unsupported');
      setMessage(
        'Voice recording is not supported on this browser — please type your message instead.',
      );
      return;
    }
    try {
      const stream = await md.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const rec = new MediaRecorder(stream);
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      recRef.current = rec;
      rec.start();
      setStatus('recording');
      setMessage('');
    } catch (err: unknown) {
      teardown();
      const name =
        err && typeof err === 'object' && 'name' in err
          ? String((err as { name?: string }).name)
          : '';
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setStatus('denied');
        setMessage(
          'Microphone access was blocked. Allow the mic in your browser settings, or just type your message.',
        );
      } else if (name === 'NotFoundError') {
        setStatus('denied');
        setMessage(
          'No microphone was found on this device — please type your message instead.',
        );
      } else {
        setStatus('error');
        setMessage(
          'Could not start recording — please try again or type your message.',
        );
      }
    }
  }, [status, teardown]);

  const stop = useCallback(async (): Promise<Blob | null> => {
    const rec = recRef.current;
    if (!rec || status !== 'recording') {
      teardown();
      setStatus('idle');
      return null;
    }
    return await new Promise<Blob | null>((resolve) => {
      rec.onstop = () => {
        const parts = chunksRef.current.slice();
        const type = rec.mimeType || 'audio/webm';
        teardown();
        setStatus('idle');
        if (parts.length === 0) {
          resolve(null);
          return;
        }
        resolve(new Blob(parts, { type }));
      };
      try {
        rec.stop();
      } catch {
        teardown();
        setStatus('idle');
        resolve(null);
      }
    });
  }, [status, teardown]);

  const cancel = useCallback(() => {
    const rec = recRef.current;
    try {
      if (rec && status === 'recording') {
        rec.onstop = null;
        rec.stop();
      }
    } catch {
      /* ignore */
    }
    teardown();
    setStatus('idle');
    setMessage('');
  }, [status, teardown]);

  return { status, message, start, stop, cancel };
}
